import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "file-operations"
    }}>{`File operations`}</h1>
    <p>{`To perform any file operations including read, write, create, and delete, UXP provides a couple of options. But before we look at the APIs, let's get acquainted with a few concepts.`}</p>
    <h2 {...{
      "id": "system-requirements"
    }}>{`System requirements`}</h2>
    <p>{`Please make sure your local environment uses the following application versions before proceeding.`}</p>
    <ul>
      <li parentName="ul">{`InDesign v18.5 or higher`}</li>
      <li parentName="ul">{`UDT v1.9.0 or higher`}</li>
      <li parentName="ul">{`Manifest version v5 or higher`}</li>
    </ul>
    <h2 {...{
      "id": "concepts"
    }}>{`Concepts`}</h2>
    <h3 {...{
      "id": "sandbox-and-other-locations"
    }}>{`Sandbox and other locations`}</h3>
    <p>{`UXP, by default, only allows access to certain locations in the user's file system. These locations are referred to as the sandbox. `}</p>
    <InlineAlert variant="info" slots="header, text1, text2" mdxType="InlineAlert" />
    <p>{`Plugins and Scripts`}</p>
    <p><strong parentName="p">{`In plugins`}</strong>{`, a sandbox is typically the plugin's folder, plus a temporary and data folder. The plugin's location is read-only. Whereas, the temporary and data folders are provided to you to store transitory information. Note that the data stored in these locations can get accidentally erased and should not be considered permanent.`}</p>
    <p><strong parentName="p">{`In scripts`}</strong>{`, a sandbox just consists of a temporary folder. It is meant to store transitory information and can get accidentally erased.`}</p>
    <p>{`However, we understand that there are circumstances when you would like to access other file locations as well. Accessing such locations is possible in UXP but you will need to seek permission first.`}</p>
    <h3 {...{
      "id": "manifest-permission"
    }}>{`Manifest permission`}</h3>
    <p>{`For file system access you require permission for the `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` module.`}</p>
    <InlineAlert variant="info" slots="header, text1, text2" mdxType="InlineAlert" />
    <p>{`Plugins and Scripts`}</p>
    <p><strong parentName="p">{`In plugins`}</strong>{`, you should seek permission for `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` in your manifest.`}<br></br>{` IMPORTANT: Please read about the `}<a parentName="p" {...{
        "href": "../../../plugins/concepts/manifest/#permissionsdefinition"
      }}>{`manifest permissions`}</a>{` module before proceeding.`}</p>
    <p><strong parentName="p">{`In scripts`}</strong>{`, the permission for `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` is fixed. You can ignore the manifest details in the following examples. Learn about these values in the `}<a parentName="p" {...{
        "href": "../../fundamentals/manifest/"
      }}>{`manifest fundamentals section`}</a>{`. `}</p>
    <p>{`Let's understand the manifest settings a bit more in detail.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`/* manifest.json */
{  
    "requiredPermissions": {
        "localFileSystem": "plugin"
    }
}
`}</code></pre>
    <p>{`Allowed values for `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`plugin`}</inlineCode>{`: Default value. Provides access to the sandbox.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`request`}</inlineCode>{`: Let's you request the user to select a location of their choice.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`fullAccess`}</inlineCode>{`: Provides full access to the user file system`}</li>
    </ul>
    <p><em parentName="p">{`Protip`}</em>{`: Make sure you pick the most accurate permission for your use case because in the future we may ask users to provide their consent based on it. You may find 'fullAccess' to be the least restrictive and hence the safest to pick, but a user may not be comfortable giving full access to their system unless it's absolutely necessary and might deny the installation of your plugin. `}</p>
    <h3 {...{
      "id": "schemes"
    }}>{`Schemes`}</h3>
    <p>{`UXP provides a shorthand representation of these locations via schemes.`}</p>
    <p>{`For sandbox, you can use `}<inlineCode parentName="p">{`plugin:/`}</inlineCode>{`, `}<inlineCode parentName="p">{`plugin-data:/`}</inlineCode>{`and `}<inlineCode parentName="p">{`plugin-temp:/`}</inlineCode>{`.
And, for other locations, use `}<inlineCode parentName="p">{`file:/`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<img src="plugin:/sample.png" />
<img src="file:/Users/user/Downloads/sample.png" /> <!-- update the path based on your system -->
`}</code></pre>
    <InlineAlert variant="info" slots="header, text1, text2" mdxType="InlineAlert" />
    <p>{`Plugins and Scripts`}</p>
    <p><strong parentName="p">{`In plugins`}</strong>{`, you should seek permission for `}<inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` in your manifest.`}<br></br>{` IMPORTANT: Please read about the `}<a parentName="p" {...{
        "href": "../../../plugins/concepts/manifest/#permissionsdefinition"
      }}>{`manifest permissions`}</a>{` module before proceeding.`}</p>
    <p><strong parentName="p">{`In scripts`}</strong>{`, you can avail only `}<inlineCode parentName="p">{`plugin-temp:/`}</inlineCode>{` to read/write from/to a temporary folder. `}</p>
    <h2 {...{
      "id": "example"
    }}>{`Example`}</h2>
    <p>{`You have two options to access the file system - `}<inlineCode parentName="p">{`LocalFileSytem`}</inlineCode>{` and `}<inlineCode parentName="p">{`FS`}</inlineCode>{` module. These modules are very similar in terms of the capabilities they offer, however, there is a difference in the way they carry out the task.`}</p>
    <p><inlineCode parentName="p">{`LocalFileSytem`}</inlineCode>{` APIs work with an object reference called `}<inlineCode parentName="p">{`Entry`}</inlineCode>{`. Having an object reference makes it easier to manage and perform multiple operations. Whereas the `}<inlineCode parentName="p">{`FS`}</inlineCode>{` APIs are very similar to NodeJS path-based file system APIs which make them ideal for carrying out single operations. `}</p>
    <h3 {...{
      "id": "localfilesytem-api"
    }}>{`LocalFileSytem API`}</h3>
    <p>{`Available via `}<inlineCode parentName="p">{`require('uxp').storage.localFileSystem`}</inlineCode>{` which returns an instance of `}<inlineCode parentName="p">{`FileSystemProvider`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "nomenclature"
    }}>{`Nomenclature`}</h4>
    <p>{`A file system is a conglomerate of files and folders. You can use the `}<inlineCode parentName="p">{`File`}</inlineCode>{` and `}<inlineCode parentName="p">{`Folder`}</inlineCode>{` classes to refer to them. However, these classes have a base class called `}<inlineCode parentName="p">{`Entry`}</inlineCode>{`. The nomenclature of some of the APIs uses 'entry' in them typically when the output type can be either of the two - `}<inlineCode parentName="p">{`File`}</inlineCode>{` or `}<inlineCode parentName="p">{`Folder`}</inlineCode>{` and be determined only at runtime. Therefore, it's a good practice to check using `}<inlineCode parentName="p">{`isfile`}</inlineCode>{` or `}<inlineCode parentName="p">{`isFolder`}</inlineCode>{` before using specific APIs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { localFileSystem, types } = require('uxp').storage;
async function foo(){
    // Create new folder and file
    try {
        const newFolderEntry = await localFileSystem.createEntryWithUrl("plugin-temp:/temp", { type: types.folder });
        if (newFolderEntry.isFolder) {
            const newFile = await newFolderEntry.createFile("temp.txt", {overwrite: true});
            await newFile.write("Sample file created.");
        }
    } catch (e) {
        console.error(e);
    }
}
`}</code></pre>
    <p>{`Now let's take a look at some examples to access system locations based on permission settings.`}</p>
    <h4 {...{
      "id": "accessing-sandbox"
    }}>{`Accessing sandbox`}</h4>
    <CodeBlock slots="heading, code" repeat="2" languages="JSON, JavaScript" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fsProvider = require('uxp').storage.localFileSystem;
async function foo() {
    // Access sandbox
    if (fsProvider.isFileSystemProvider) {
        try {
            const pluginFolder = await fsProvider.getEntryWithUrl("plugin-data:/");
            console.log(\`File path: \${pluginFolder.nativePath}\`);
        } catch (e) {
            console.error(e);
        }
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "plugin"
    }
}
`}</code></pre>
    <h4 {...{
      "id": "accessing-other-locations"
    }}>{`Accessing other locations`}</h4>
    <CodeBlock slots="heading, code" repeat="2" languages="JSON, JavaScript" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-1"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fsProvider = require('uxp').storage.localFileSystem;
async function foo() {
    // Access other location
    if (fsProvider.isFileSystemProvider) {
        try {
            const pluginFolder = await fsProvider.getEntryWithUrl("file:/Users/user/Documents"); // update the path based on your system
            console.log(\`File path: \${pluginFolder.nativePath}\`);
        } catch (e) {
            console.error(e);
        }
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-1"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "fullAccess"
    }
}
`}</code></pre>
    <h4 {...{
      "id": "users-choice-of-location"
    }}>{`User's choice of location`}</h4>
    <p>{`These APIs are particularly handy when you want to request the user to select a folder/file location of their choice. For example, the below code snippet using `}<inlineCode parentName="p">{`getFileForOpening`}</inlineCode>{` and `}<inlineCode parentName="p">{`getFileForSaving`}</inlineCode>{` methods presents a file picker for the user to choose from.`}</p>
    <CodeBlock slots="heading, code" repeat="2" languages="JSON, JavaScript" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-2"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fsProvider = require('uxp').storage.localFileSystem;

async function foo() {
    // Ask user to select a file. Show their 'Desktop' as the default folder.
    if (fsProvider.isFileSystemProvider) {
        const { domains, fileTypes } = require('uxp').storage;

        try {
            const file = await fsProvider.getFileForOpening({ initialDomain: domains.userDesktop, types: fileTypes.text });
            if (!file) {
                console.error("Something went wrong.");
                return;
            }

            // read the file content
            const text = await file.read();
            console.log(\`File content: \${text}\`);
        } catch (err) {
            console.error(err);
        }
    }
}

async function bar() {
    // Ask user to select a location to save a file
    if (fsProvider.isFileSystemProvider) {
        try {
            const file = await fsProvider.getFileForSaving("sample.txt", { types: ["txt"] });
            if (!file) {
                console.error("Something went wrong.");
                return;
            }

            // write content to file
            await file.write("UXP saved sample file.");
        } catch (err) {
            console.error(err);
        }        
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-2"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "request"
    }
}
`}</code></pre>
    <h4 {...{
      "id": "save-users-choice-of-location"
    }}>{`Save user's choice of location`}</h4>
    <p>{`If you would like to remember the user's choice for an extended period, you can do it with the help of a token. There are two types of tokens you can create`}</p>
    <ul>
      <li parentName="ul">{`Session token - has a shorter span. It lasts until the plugin is 'Unloaded' from UDT or the script finishes its execution or the application is closed. `}</li>
      <li parentName="ul">{`Persistent token - is more permanent in nature, and can last for multiple sessions or until the plugin is 'Uninstalled'. (Since scripts only last for the time of their execution, you should not use persistent tokens in scripts.)`}</li>
    </ul>
    <p>{`The example below shows the essence of this usage but you should ideally save these tokens in the storage (more details covered in `}<a parentName="p" {...{
        "href": "./storage.md"
      }}>{`Storage`}</a>{` section) for later use.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fsProvider = require('uxp').storage.localFileSystem;

const { domains, fileTypes } = require('uxp').storage;
const entry = await fsProvider.getFileForOpening({ initialDomain: domains.userDesktop, types: fileTypes.text });
const token = await fsProvider.createPersistentToken(entry); // store for future use

async function readFileUsingTokensInLocalFileSystem() {
    // In the future, access the entry using the token directly
    try {
        const file = await fsProvider.getEntryForPersistentToken(token);
        const text = await file.read();
        console.log(\`File content: \${text}\`);
    } catch (err) {
        console.error(err);
    }
}
`}</code></pre>
    <h4 {...{
      "id": "reference-material"
    }}>{`Reference material`}</h4>
    <ul>
      <li parentName="ul">{`Instance of `}<a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/uxp/Persistent%20File%20Storage/FileSystemProvider/"
        }}>{`localFileSystem`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/uxp/Persistent%20File%20Storage/Entry/"
        }}>{`Entry`}</a>{` and `}<a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/uxp/Persistent%20File%20Storage/EntryMetadata/"
        }}>{`Entry metadata`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/uxp/Persistent%20File%20Storage/File/"
        }}>{`File`}</a>{` and `}<a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/uxp/Persistent%20File%20Storage/Folder/"
        }}>{`Folder`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/Path/"
        }}>{`Path`}</a></li>
    </ul>
    <h3 {...{
      "id": "fs-module"
    }}>{`FS Module`}</h3>
    <p>{`Based on NodeJS file system APIs, these provide direct access to file locations with the help of schema.`}</p>
    <p><strong parentName="p">{`Accessing sandbox`}</strong></p>
    <CodeBlock slots="heading, code" repeat="2" languages="JavaScript, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-3"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fs = require("fs");
async function foo() {
    // Read a file from sandbox using fs module
    try {
        const text = await fs.readFile("plugin:/sample.txt", 'utf8');
        console.log(\`File content: \${text}\`);
    } catch (e) {
        console.error(e);
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-3"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "plugin"
    }
}
`}</code></pre>
    <p><strong parentName="p">{`Accessing other locations`}</strong></p>
    <CodeBlock slots="heading, code" repeat="2" languages="JavaScript, JSON" mdxType="CodeBlock" />
    <h4 {...{
      "id": "javascript-4"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fs = require("fs");
async function foo() {
    // Write to a arbitrary location using fs module
    try {
        await fs.writeFile("/Users/user/Desktop/output.txt", "This is a sample text.", {encoding: "utf-8"}); // update the path based on your system
    } catch (e) {
        console.error(e);
    }
}
`}</code></pre>
    <h4 {...{
      "id": "manifest-4"
    }}>{`manifest`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "fullAccess"
    }
}
`}</code></pre>
    <h4 {...{
      "id": "reference-material-1"
    }}>{`Reference material`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Modules/fs/"
        }}>{`FS module`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/indesign/uxp/indesign/uxp/reference/uxp-api/reference-js/Global%20Members/Path/"
        }}>{`Path`}</a></li>
    </ul>
    <h2 {...{
      "id": "additional-notes"
    }}>{`Additional Notes`}</h2>
    <ul>
      <li parentName="ul">{`Despite the manifest setting `}<inlineCode parentName="li">{`fullAccess`}</inlineCode>{`, certain files and folders may still not be accessible. It depends on the permission of the Operating System. For Win32 and Mac, these APIs can access anywhere in the file system. But, in UWP, accessing the system folder is prohibited.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      